import { logger } from './logger.util';
import type { FileFormat } from '../types/fileDownload.type';
import { FILE_EXTENSIONS } from '../constants/file.constant';

type DownloadParams = {
  url: string;
  fileFormat?: FileFormat;
  fileName?: string;
  forceDownload?: boolean;
};

/**
 * Descarga un archivo desde una URL directa
 * @param params - Parámetros para la descarga
 * @param params.url - URL directa del archivo (puede ser URL firmada de S3)
 * @param params.fileFormat - Formato del archivo ('PDF' o 'XLS') para mejor detección
 * @param params.fileName - Nombre del archivo a descargar (sin extensión, opcional, por defecto 'file')
 * @param params.forceDownload - Si es `true`, un PDF se descarga directo en vez de abrirse en una pestaña nueva (por defecto `false`, mantiene el comportamiento de visor)
 * @returns boolean - true si el proceso se inició correctamente
 */
export async function downloadFromUrl({
  url,
  fileFormat,
  fileName = 'file',
  forceDownload = false,
}: DownloadParams): Promise<boolean> {
  try {
    const isPDF = fileFormat?.toUpperCase() === FILE_EXTENSIONS.PDF;
    const isXLSOrCSV = fileFormat
      ? (
          [
            FILE_EXTENSIONS.XLS,
            FILE_EXTENSIONS.XLSX,
            FILE_EXTENSIONS.CSV,
          ] as string[]
        ).includes(fileFormat.toUpperCase())
      : false;

    if (fileFormat && isPDF && !forceDownload) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (fileFormat && (isXLSOrCSV || isPDF)) {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fileName}.${fileFormat.toLowerCase()}`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } else {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';

      iframe.src = url;
      document.body.appendChild(iframe);

      setTimeout(() => {
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 3000);
    }

    return true;
  } catch (error) {
    logger.error('Error al procesar archivo:', { error }, error as Error);
    return false;
  }
}
